import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getContentById,
  getVideoQueueItemById,
  updateGeneratedContent,
  updateVideoQueueItem,
  callRespinContent,
  callGenerateImage,
  callPublishContent,
  callRepurposeContent,
  getRepurposedContent,
  callPostTweetThread,
  sendToVideoQueue,
  callCropImage,
  callGenerateVoiceover,
  callGenerateAvatarVideo,
  uploadSelfRecording,
  uploadCustomImage
} from '../../services/contentOpsService';
import VersionHistory from './VersionHistory';
import ImageGallery from './ImageGallery';
import TweetThreadEditor from './TweetThreadEditor';
import EditorTabs from './EditorTabs';
import WorkflowProgress from './WorkflowProgress';

const FORMAT_LABELS = {
  blog: 'Blog Post',
  tweet: 'Tweet',
  yt_script: 'YouTube Script',
  yt_script_short: 'Short Video Script',
  yt_script_long: 'Long Video Script',
  ig: 'Instagram Caption',
  tweet_thread: 'Tweet Thread',
  ig_idea: 'IG Idea',
  idea_board: 'Idea Board',
  chess_clip: 'Chess Clip'
};

const IMAGE_PRESETS = [
  { value: 'blog_hero', label: 'Blog Hero' },
  { value: 'social_card', label: 'Social Card' },
  { value: 'ig_post', label: 'IG Post' },
  { value: 'rankings_card', label: 'Rankings Card' }
];

const formatDate = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const ContentEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Core state
  const [content, setContent] = useState(null);
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Respin state
  const [feedback, setFeedback] = useState('');
  const [respinning, setRespinning] = useState(false);

  // Image state
  const [imagePrompt, setImagePrompt] = useState('');
  const [imagePreset, setImagePreset] = useState('blog_hero');
  const [generatingImage, setGeneratingImage] = useState(false);
  const [referenceImage, setReferenceImage] = useState(null); // { base64, mimeType, preview }

  // Publish state
  const [author, setAuthor] = useState('TrendingChess AI');
  const [tags, setTags] = useState('');
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [publishing, setPublishing] = useState(false);
  const [sendingToQueue, setSendingToQueue] = useState(false);

  // Repurpose state
  const [repurposing, setRepurposing] = useState(null);
  const [repurposedItems, setRepurposedItems] = useState([]);

  // Image cropping state
  const [croppingImage, setCroppingImage] = useState(false);
  const [cropPreset, setCropPreset] = useState('social_card');
  const [croppedImages, setCroppedImages] = useState([]);

  // Image upload state
  const [uploadingImage, setUploadingImage] = useState(false);

  // Thread posting state
  const [postingThread, setPostingThread] = useState(false);

  // Video caption state (for chess clips and video content)
  const [caption, setCaption] = useState('');
  const [publishingVideo, setPublishingVideo] = useState(false);
  const [videoPlatforms, setVideoPlatforms] = useState({
    youtube: true,
    instagram: true,
    tiktok: true
  });

  // Parsed creative suggestions state (for yt_script)
  const [parsedCreative, setParsedCreative] = useState(null);
  const [editableImagePrompt, setEditableImagePrompt] = useState('');
  const [editableThumbnailIdeas, setEditableThumbnailIdeas] = useState([]);
  const [editableRemotionProps, setEditableRemotionProps] = useState({});

  // New YT script format state
  const [editableTitle, setEditableTitle] = useState('');
  const [editableHook, setEditableHook] = useState('');
  const [editableSpokenScript, setEditableSpokenScript] = useState('');
  const [editableVideoPacing, setEditableVideoPacing] = useState([]);

  // Tab navigation state
  const [activeTab, setActiveTab] = useState(null);

  // Video pipeline state
  const [videoQueueData, setVideoQueueData] = useState(null);
  const [generatingVoiceover, setGeneratingVoiceover] = useState(false);
  const [generatingAvatar, setGeneratingAvatar] = useState(false);
  const [uploadingSelfRecording, setUploadingSelfRecording] = useState(false);

  const loadContent = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      // First try generated_content
      let doc = await getContentById(id);

      // If not found, try video_queue (for chess clips and orphan videos)
      if (!doc) {
        const videoDoc = await getVideoQueueItemById(id);
        if (videoDoc) {
          // Map video_queue fields to content-like structure
          doc = {
            ...videoDoc,
            topic: videoDoc.title || 'Untitled',
            body: videoDoc.description || videoDoc.script || '',
            format: videoDoc.type === 'chess_clip' ? 'chess_clip' : 'video_queue',
            status: videoDoc.status || 'draft',
            isVideoQueueItem: true,
          };
        }
      }

      if (!doc) {
        setError('Content not found');
        return;
      }
      setContent(doc);
      setBody(doc.body || '');
      setCaption(doc.caption || '');
      if (doc.suggestedImagePrompt && !doc.images?.length) {
        setImagePrompt(doc.suggestedImagePrompt);
      }
      // Load cropped images if they exist
      if (doc.croppedImages?.length > 0) {
        setCroppedImages(doc.croppedImages);
      }
      setDirty(false);
    } catch (err) {
      console.error('Load content error:', err);
      setError('Failed to load content');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadRepurposed = useCallback(async () => {
    try {
      const items = await getRepurposedContent(id);
      setRepurposedItems(items);
    } catch (err) {
      console.error('Load repurposed error:', err);
    }
  }, [id]);

  // Load video queue data when content has a videoQueueId
  const loadVideoQueueData = useCallback(async () => {
    if (!content?.videoQueueId) {
      setVideoQueueData(null);
      return;
    }
    try {
      const data = await getVideoQueueItemById(content.videoQueueId);
      setVideoQueueData(data);
    } catch (err) {
      console.error('Load video queue error:', err);
    }
  }, [content?.videoQueueId]);

  useEffect(() => { loadContent(); }, [loadContent]);
  useEffect(() => { loadRepurposed(); }, [loadRepurposed]);
  useEffect(() => { loadVideoQueueData(); }, [loadVideoQueueData]);

  // Get tabs configuration based on format
  const getTabsForFormat = useCallback((format, contentData) => {
    const bodyLen = (contentData?.body?.length || 0);
    const hasImage = !!contentData?.selectedImageUrl;
    const spokenLen = (editableSpokenScript?.length || contentData?.spokenScript?.length || 0);
    const hasImagePrompt = !!(editableImagePrompt || contentData?.imagePrompt);
    const hasThumbnailIdeas = (editableThumbnailIdeas?.length || contentData?.thumbnailIdeas?.length || 0) > 0;
    const captionLen = (caption?.length || contentData?.caption?.length || 0);

    switch (format) {
      case 'blog':
        return [
          { id: 'content', label: 'Content', required: true, complete: bodyLen > 100 },
          { id: 'image', label: 'Image', required: true, complete: hasImage },
          { id: 'publish', label: 'Publish', required: false },
          { id: 'extras', label: 'Extras', required: false }
        ];
      case 'tweet':
        return [
          { id: 'content', label: 'Content', required: true, complete: bodyLen >= 10 },
          { id: 'image', label: 'Image', required: false },
          { id: 'publish', label: 'Publish', required: false },
          { id: 'extras', label: 'Extras', required: false }
        ];
      case 'tweet_thread':
        return [
          { id: 'content', label: 'Content', required: true, complete: bodyLen >= 20 },
          { id: 'image', label: 'Image', required: false },
          { id: 'publish', label: 'Publish', required: false },
          { id: 'extras', label: 'Extras', required: false }
        ];
      case 'yt_script':
      case 'yt_script_short':
      case 'yt_script_long':
        return [
          { id: 'script', label: 'Script', required: true, complete: spokenLen > 50 },
          { id: 'thumbnail', label: 'Thumbnail', required: true, complete: hasImagePrompt || hasThumbnailIdeas },
          { id: 'queue', label: 'Video Queue', required: false },
          { id: 'extras', label: 'Extras', required: false }
        ];
      case 'ig':
        return [
          { id: 'content', label: 'Content', required: true, complete: bodyLen >= 50 },
          { id: 'image', label: 'Image', required: true, complete: hasImage },
          { id: 'publish', label: 'Publish', required: false },
          { id: 'extras', label: 'Extras', required: false }
        ];
      case 'chess_clip':
        return [
          { id: 'preview', label: 'Preview', required: false },
          { id: 'caption', label: 'Caption', required: true, complete: captionLen > 10 },
          { id: 'publish', label: 'Publish', required: false }
        ];
      default:
        return [
          { id: 'content', label: 'Content', required: true, complete: bodyLen > 0 },
          { id: 'extras', label: 'Extras', required: false }
        ];
    }
  }, [editableSpokenScript, editableImagePrompt, editableThumbnailIdeas, caption]);

  // Compute current tabs
  const tabs = useMemo(() => {
    if (!content) return [];
    return getTabsForFormat(content.format, content);
  }, [content, getTabsForFormat, body, caption]);

  // Set initial active tab when content loads
  useEffect(() => {
    if (content && !activeTab && tabs.length > 0) {
      setActiveTab(tabs[0].id);
    }
  }, [content, activeTab, tabs]);

  // Parse creative suggestions and new format fields from content
  useEffect(() => {
    if (!content) return;

    // For yt_script format, parse new format fields
    if (['yt_script', 'yt_script_short', 'yt_script_long'].includes(content.format)) {
      // Try to get fields from content (stored at top level) or from scriptData
      const scriptData = content.scriptData || {};

      // Parse from body if not stored at top level
      let parsedBody = null;
      if (body) {
        try {
          let jsonStr = body;
          if (body.includes('```')) {
            const match = body.match(/```(?:json)?\s*([\s\S]*?)```/);
            if (match) jsonStr = match[1];
          }
          parsedBody = JSON.parse(jsonStr.trim());
        } catch (e) {
          // Not JSON or parsing failed
        }
      }

      // Set title (prefer top-level, then scriptData, then parsed body)
      const title = content.title || scriptData.title || parsedBody?.title || content.topic || '';
      setEditableTitle(title);

      // Set hook
      const hook = content.hook || scriptData.hook || parsedBody?.hook || '';
      setEditableHook(hook);

      // Set spoken script (prefer top-level, then scriptData, then parsed body, then extract from legacy sections)
      let spokenScript = content.spokenScript || scriptData.spokenScript || parsedBody?.spokenScript;
      if (!spokenScript && (scriptData.sections || parsedBody?.sections)) {
        // Extract from legacy sections format
        const sections = scriptData.sections || parsedBody?.sections || [];
        spokenScript = sections
          .filter(s => s.type === 'spoken')
          .map(s => s.text)
          .join('\n\n');
      }
      setEditableSpokenScript(spokenScript || '');

      // Set video pacing (new format) or leave empty for legacy
      const videoPacing = content.videoPacing || scriptData.videoPacing || parsedBody?.videoPacing || [];
      setEditableVideoPacing(videoPacing);

      // Set thumbnail ideas
      const thumbnailIdeas = content.thumbnailIdeas || scriptData.thumbnailIdeas || parsedBody?.thumbnailIdeas ||
        content.creativeSuggestions?.thumbnailIdeas || [];
      setEditableThumbnailIdeas(thumbnailIdeas);

      // Set image prompt
      const imagePrompt = content.imagePrompt || scriptData.imagePrompt || parsedBody?.imagePrompt ||
        content.creativeSuggestions?.imagePrompt || '';
      setEditableImagePrompt(imagePrompt);

      // Build creative object for display (combining old and new format data)
      const creative = {
        thumbnailIdeas,
        imagePrompt,
        remotion: content.creativeSuggestions?.remotion || scriptData.remotion || parsedBody?.remotion || null,
        preset: content.creativeSuggestions?.preset || 'social_card'
      };
      if (creative.remotion || creative.imagePrompt || creative.thumbnailIdeas?.length) {
        setParsedCreative(creative);
        setEditableRemotionProps(creative.remotion?.props || {});
      }
    } else {
      // Non yt_script formats: original logic for creative suggestions
      let creative = content.creativeSuggestions;
      const hasCreativeContent = creative &&
        (creative.remotion || creative.imagePrompt || creative.thumbnailIdeas?.length);

      if (hasCreativeContent) {
        setParsedCreative(creative);
        setEditableImagePrompt(creative.imagePrompt || '');
        setEditableThumbnailIdeas(creative.thumbnailIdeas || []);
        setEditableRemotionProps(creative.remotion?.props || {});
      }
    }
  }, [content, body]);

  // ── Save Draft ──
  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      await updateGeneratedContent(id, {
        body,
        lastEditedAt: new Date()
      });
      setContent(prev => ({ ...prev, body, lastEditedAt: new Date() }));
      setDirty(false);
    } catch (err) {
      setError('Failed to save draft');
    } finally {
      setSaving(false);
    }
  };

  // ── Respin with AI ──
  const handleRespin = async () => {
    if (!feedback.trim()) return;
    try {
      setRespinning(true);
      setError('');
      const result = await callRespinContent(id, body, feedback.trim());
      setBody(result.body);
      setContent(prev => ({
        ...prev,
        body: result.body,
        version: result.version,
        versions: [...(prev.versions || []), {
          version: result.previousVersion,
          body: prev.body,
          feedback: feedback.trim(),
          createdAt: new Date()
        }]
      }));
      setFeedback('');
      setDirty(false);
    } catch (err) {
      setError(err.message || 'Failed to respin content');
    } finally {
      setRespinning(false);
    }
  };

  // ── Restore Version ──
  const handleRestore = (restoredBody, fromVersion) => {
    setBody(restoredBody);
    setDirty(true);
  };

  // ── Reference Image Upload ──
  const handleReferenceImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Reference image must be PNG, JPEG, or WebP');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Reference image must be under 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const base64Full = reader.result;
      const base64 = base64Full.split(',')[1]; // Remove data:mime;base64, prefix
      setReferenceImage({
        base64,
        mimeType: file.type,
        preview: base64Full
      });
    };
    reader.readAsDataURL(file);
  };

  const clearReferenceImage = () => {
    setReferenceImage(null);
  };

  // ── Image Generation ──
  const handleGenerateImage = async () => {
    if (!imagePrompt.trim()) return;
    try {
      setGeneratingImage(true);
      setError('');
      const result = await callGenerateImage(
        imagePrompt.trim(),
        imagePreset,
        id,
        referenceImage ? { base64: referenceImage.base64, mimeType: referenceImage.mimeType } : null,
        content.selectedImageUrl || null  // Pass selected image for refinement feedback loop
      );
      // Append to content images array
      const newImage = {
        imageUrl: result.imageUrl,
        prompt: imagePrompt.trim(),
        preset: imagePreset,
        feedback: null,
        hasReference: !!referenceImage,
        iteration: (content.images?.length || 0) + 1,
        createdAt: new Date()
      };
      const updatedImages = [...(content.images || []), newImage];
      const imgUpdates = { images: updatedImages };
      if (!content.selectedImageUrl) {
        imgUpdates.selectedImageUrl = result.imageUrl;
      }
      await updateGeneratedContent(id, imgUpdates);
      setContent(prev => ({ ...prev, ...imgUpdates }));
    } catch (err) {
      setError(err.message || 'Failed to generate image');
    } finally {
      setGeneratingImage(false);
    }
  };

  const handleSelectImage = async (imageUrl) => {
    try {
      await updateGeneratedContent(id, { selectedImageUrl: imageUrl });
      setContent(prev => ({ ...prev, selectedImageUrl: imageUrl }));
    } catch (err) {
      setError('Failed to select image');
    }
  };

  // ── Upload Custom Image ──
  const handleUploadCustomImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploadingImage(true);
      setError('');
      await uploadCustomImage(id, file);
      await loadContent();
    } catch (err) {
      setError(err.message || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  // ── Crop Image to Selected Size ──
  const handleCropImage = async () => {
    if (!content.selectedImageUrl) {
      setError('Select an image first');
      return;
    }
    try {
      setCroppingImage(true);
      setError('');
      const result = await callCropImage(content.selectedImageUrl, cropPreset, id);
      // Add to cropped images (replace if same preset exists)
      const existingCropped = croppedImages.filter(img => img.preset !== cropPreset);
      const updatedCropped = [...existingCropped, result];
      setCroppedImages(updatedCropped);
      // Store cropped images in content
      await updateGeneratedContent(id, { croppedImages: updatedCropped });
      setContent(prev => ({ ...prev, croppedImages: updatedCropped }));
    } catch (err) {
      setError(err.message || 'Failed to crop image');
    } finally {
      setCroppingImage(false);
    }
  };

  // ── Publishing ──
  const handlePublish = async () => {
    if (selectedChannels.length === 0) return;
    try {
      setPublishing(true);
      setError('');
      const parsedTags = tags.split(',').map(t => t.trim()).filter(Boolean);
      await callPublishContent(id, selectedChannels, author.trim() || 'TrendingChess AI', parsedTags);
      await loadContent();
      setSelectedChannels([]);
    } catch (err) {
      setError(err.message || 'Failed to publish');
    } finally {
      setPublishing(false);
    }
  };

  // ── Send to Video Queue ──
  const handleSendToVideoQueue = async () => {
    try {
      setSendingToQueue(true);
      setError('');
      await sendToVideoQueue(id);
      await loadContent();
      await loadVideoQueueData();
    } catch (err) {
      setError(err.message || 'Failed to send to video queue');
    } finally {
      setSendingToQueue(false);
    }
  };

  // ── Video Pipeline Actions ──
  const handleGenerateVoiceover = async () => {
    if (!content?.videoQueueId) return;
    try {
      setGeneratingVoiceover(true);
      setError('');
      await callGenerateVoiceover(content.videoQueueId);
      await loadVideoQueueData();
    } catch (err) {
      setError(err.message || 'Failed to generate voiceover');
    } finally {
      setGeneratingVoiceover(false);
    }
  };

  const handleGenerateAvatar = async () => {
    if (!content?.videoQueueId) return;
    try {
      setGeneratingAvatar(true);
      setError('');
      await callGenerateAvatarVideo(content.videoQueueId);
      await loadVideoQueueData();
    } catch (err) {
      setError(err.message || 'Failed to generate avatar video');
    } finally {
      setGeneratingAvatar(false);
    }
  };

  const handleUploadSelfRecording = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !content?.videoQueueId) return;
    try {
      setUploadingSelfRecording(true);
      setError('');
      await uploadSelfRecording(content.videoQueueId, file);
      await loadVideoQueueData();
    } catch (err) {
      setError(err.message || 'Failed to upload self recording');
    } finally {
      setUploadingSelfRecording(false);
    }
  };

  // ── Repurposing ──
  const handleRepurpose = async (targetFormat) => {
    try {
      setRepurposing(targetFormat);
      setError('');
      const result = await callRepurposeContent(id, targetFormat);
      await loadRepurposed();
      // Open in new tab so the user stays on the current editor
      if (result.contentId) {
        window.open(`/admin/content-ops/editor/${result.contentId}`, '_blank');
      }
    } catch (err) {
      setError(err.message || 'Failed to repurpose content');
    } finally {
      setRepurposing(null);
    }
  };

  // ── Post Tweet Thread ──
  const handlePostThread = async () => {
    try {
      setPostingThread(true);
      setError('');
      await callPostTweetThread(id);
      await loadContent();
    } catch (err) {
      setError(err.message || 'Failed to post thread');
    } finally {
      setPostingThread(false);
    }
  };

  // ── Save Caption (for video content) ──
  const handleSaveCaption = async () => {
    try {
      setSaving(true);
      setError('');
      if (content.isVideoQueueItem) {
        await updateVideoQueueItem(id, { caption });
      } else {
        await updateGeneratedContent(id, { caption });
      }
      setContent(prev => ({ ...prev, caption }));
      setDirty(false);
    } catch (err) {
      setError('Failed to save caption');
    } finally {
      setSaving(false);
    }
  };

  // ── Publish Video ──
  const handlePublishVideo = async () => {
    const selectedPlatforms = Object.entries(videoPlatforms)
      .filter(([_, checked]) => checked)
      .map(([platform]) => platform);

    if (selectedPlatforms.length === 0) {
      setError('Please select at least one platform');
      return;
    }

    try {
      setPublishingVideo(true);
      setError('');
      await callPublishContent(id, selectedPlatforms);
      await loadContent();
    } catch (err) {
      setError(err.message || 'Failed to publish video');
    } finally {
      setPublishingVideo(false);
    }
  };

  // ── Save Creative Suggestions (and script data for yt_script) ──
  const handleSaveCreative = async () => {
    try {
      setSaving(true);
      setError('');

      const updates = {};

      // For yt_script format, save all the new fields
      if (['yt_script', 'yt_script_short', 'yt_script_long'].includes(content.format)) {
        updates.title = editableTitle;
        updates.hook = editableHook;
        updates.spokenScript = editableSpokenScript;
        updates.videoPacing = editableVideoPacing;
        updates.thumbnailIdeas = editableThumbnailIdeas;
        updates.imagePrompt = editableImagePrompt;

        // Also update scriptData for consistency
        updates.scriptData = {
          ...(content.scriptData || {}),
          title: editableTitle,
          hook: editableHook,
          spokenScript: editableSpokenScript,
          videoPacing: editableVideoPacing,
          thumbnailIdeas: editableThumbnailIdeas,
          imagePrompt: editableImagePrompt
        };

        // Update creativeSuggestions for backward compatibility
        updates.creativeSuggestions = {
          thumbnailIdeas: editableThumbnailIdeas,
          imagePrompt: editableImagePrompt,
          preset: 'social_card',
          remotion: parsedCreative?.remotion ? {
            ...parsedCreative.remotion,
            props: editableRemotionProps
          } : null
        };
      } else {
        // Non yt_script formats: original behavior
        const updatedCreative = {
          ...parsedCreative,
          imagePrompt: editableImagePrompt,
          thumbnailIdeas: editableThumbnailIdeas,
          remotion: parsedCreative?.remotion ? {
            ...parsedCreative.remotion,
            props: editableRemotionProps
          } : null
        };
        updates.creativeSuggestions = updatedCreative;
      }

      await updateGeneratedContent(id, updates);
      setContent(prev => ({ ...prev, ...updates }));
      if (updates.creativeSuggestions) {
        setParsedCreative(updates.creativeSuggestions);
      }
      setDirty(false);
    } catch (err) {
      setError('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // ── Update thumbnail idea ──
  const updateThumbnailIdea = (index, value) => {
    const updated = [...editableThumbnailIdeas];
    updated[index] = value;
    setEditableThumbnailIdeas(updated);
    setDirty(true);
  };

  // ── Add thumbnail idea ──
  const addThumbnailIdea = () => {
    setEditableThumbnailIdeas([...editableThumbnailIdeas, '']);
    setDirty(true);
  };

  // ── Remove thumbnail idea ──
  const removeThumbnailIdea = (index) => {
    setEditableThumbnailIdeas(editableThumbnailIdeas.filter((_, i) => i !== index));
    setDirty(true);
  };

  // ── Video Pacing Helpers ──
  const updateVideoPacingScene = (index, field, value) => {
    const updated = [...editableVideoPacing];
    updated[index] = { ...updated[index], [field]: value };
    setEditableVideoPacing(updated);
    setDirty(true);
  };

  // ── Sync spoken script to scene narrations ──
  const syncScriptToScenes = () => {
    if (!editableSpokenScript.trim()) return;

    // Split by paragraphs (double newlines) or sentences if no paragraphs
    const paragraphs = editableSpokenScript.split(/\n\n+/).filter(p => p.trim());

    if (editableVideoPacing.length === 0) {
      // Create scenes from paragraphs
      const newScenes = paragraphs.map((text, idx) => ({
        startTime: '',
        endTime: '',
        narration: text.trim(),
        visual: '',
        textOverlay: null,
        transition: 'cut'
      }));
      setEditableVideoPacing(newScenes);
    } else {
      // Distribute script across existing scenes
      const updated = editableVideoPacing.map((scene, idx) => ({
        ...scene,
        narration: paragraphs[idx] ? paragraphs[idx].trim() : ''
      }));
      // If more paragraphs than scenes, combine remaining into last scene
      if (paragraphs.length > editableVideoPacing.length) {
        const remaining = paragraphs.slice(editableVideoPacing.length).join('\n\n');
        updated[updated.length - 1].narration += '\n\n' + remaining;
      }
      setEditableVideoPacing(updated);
    }
    setDirty(true);
  };

  const addVideoPacingScene = () => {
    const lastScene = editableVideoPacing[editableVideoPacing.length - 1];
    const newStartTime = lastScene?.endTime || '0:00';
    setEditableVideoPacing([...editableVideoPacing, {
      startTime: newStartTime,
      endTime: '',
      narration: '',
      visual: '',
      textOverlay: null,
      transition: 'cut'
    }]);
    setDirty(true);
  };

  const removeVideoPacingScene = (index) => {
    setEditableVideoPacing(editableVideoPacing.filter((_, i) => i !== index));
    setDirty(true);
  };

  // ── Word count helper ──
  const wordCount = (text) => {
    if (!text) return 0;
    return text.trim().split(/\s+/).filter(Boolean).length;
  };

  // ── Render ──
  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="max-w-4xl mx-auto space-y-4">
        <p className="text-red-400">{error || 'Content not found'}</p>
        <button onClick={() => navigate('/admin/content-ops')} className="text-blue-400 hover:text-blue-300 text-sm">
          ← Back to Content Ops
        </button>
      </div>
    );
  }

  // Chess Clip View - simplified editor for video_queue chess clips
  if (content.format === 'chess_clip' || content.isVideoQueueItem) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Error Banner */}
        {error && (
          <div className="bg-red-900/50 border border-red-700 text-red-300 px-4 py-3 rounded-lg flex justify-between items-center">
            <span>{error}</span>
            <button onClick={() => setError('')} className="text-red-400 hover:text-red-200">✕</button>
          </div>
        )}

        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <button
              onClick={() => navigate('/admin/content')}
              className="text-gray-400 hover:text-gray-300 text-sm mb-2 inline-block"
            >
              ← Back to Content
            </button>
            <h1 className="text-2xl font-bold text-gray-100">{content.title || content.topic || 'Untitled'}</h1>
            <div className="flex items-center gap-3 mt-2">
              <span className="text-xs px-2 py-0.5 rounded bg-amber-600/20 text-amber-400">
                {content.status || 'chess_clip'}
              </span>
              <span className="text-xs px-2 py-0.5 rounded bg-emerald-600/20 text-emerald-400">
                Chess Clip
              </span>
              {content.source && (
                <span className="text-xs text-gray-500">
                  Source: {content.source}
                  {content.sourceUrl && (
                    <a href={content.sourceUrl} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 ml-1">↗</a>
                  )}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <EditorTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

        {/* ═══ PREVIEW TAB ═══ */}
        {activeTab === 'preview' && (
          <div className="space-y-4">
            {/* Video Player */}
            {content.videoUrl && (
              <section className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-300 mb-3">Video Preview</h3>
                <video
                  controls
                  src={content.videoUrl}
                  className="w-full rounded max-h-[480px] bg-black"
                />
              </section>
            )}

            {/* Player Info */}
            {(content.whitePlayer || content.blackPlayer) && (
              <section className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-300 mb-3">Game Info</h3>
                <div className="flex gap-6 text-sm">
                  {content.whitePlayer && (
                    <div>
                      <span className="text-gray-500">White:</span>
                      <span className="text-gray-200 ml-2">{content.whitePlayer}</span>
                    </div>
                  )}
                  {content.blackPlayer && (
                    <div>
                      <span className="text-gray-500">Black:</span>
                      <span className="text-gray-200 ml-2">{content.blackPlayer}</span>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Moves / PGN */}
            {(content.moves || content.pgn) && (
              <section className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-300 mb-3">Moves</h3>
                {content.moves && Array.isArray(content.moves) && (
                  <p className="text-sm text-gray-400 font-mono">
                    {content.moves.join(' ')}
                  </p>
                )}
                {content.pgn && (
                  <pre className="text-xs text-gray-400 bg-gray-900 rounded p-3 mt-2 whitespace-pre-wrap overflow-x-auto">
                    {content.pgn}
                  </pre>
                )}
              </section>
            )}

            {/* Description */}
            {content.description && (
              <section className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-300 mb-3">Description</h3>
                <p className="text-sm text-gray-400">{content.description}</p>
              </section>
            )}

            {/* Metadata */}
            <section className="bg-gray-800 border border-gray-700 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-300 mb-3">Details</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Created:</span>
                  <span className="text-gray-300 ml-2">{formatDate(content.createdAt)}</span>
                </div>
                <div>
                  <span className="text-gray-500">ID:</span>
                  <span className="text-gray-400 ml-2 font-mono text-xs">{content.id}</span>
                </div>
                {content.storagePath && (
                  <div className="col-span-2">
                    <span className="text-gray-500">Storage:</span>
                    <span className="text-gray-400 ml-2 font-mono text-xs">{content.storagePath}</span>
                  </div>
                )}
              </div>
            </section>
          </div>
        )}

        {/* ═══ CAPTION TAB ═══ */}
        {activeTab === 'caption' && (
          <section className="bg-gray-800 border border-gray-700 rounded-lg p-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-sm font-medium text-gray-300">Caption</h3>
              {dirty && <span className="text-xs text-yellow-400">Unsaved</span>}
            </div>
            <textarea
              rows={4}
              value={caption}
              onChange={(e) => { setCaption(e.target.value); setDirty(true); }}
              placeholder="Short, fun caption for social media..."
              className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-gray-200 text-sm focus:outline-none focus:border-blue-500 resize-y"
            />
            <div className="flex justify-between items-center mt-2">
              <p className="text-xs text-gray-500">Used for YouTube, Instagram, TikTok</p>
              <button
                onClick={handleSaveCaption}
                disabled={saving || !dirty}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-3 py-1.5 rounded text-sm transition-colors"
              >
                {saving ? 'Saving...' : 'Save Caption'}
              </button>
            </div>
            {caption.length > 10 && (
              <p className="text-xs text-green-400 mt-2">Caption complete</p>
            )}
            {caption.length > 0 && caption.length <= 10 && (
              <p className="text-xs text-yellow-400 mt-2">Caption too short (min 10 characters)</p>
            )}
          </section>
        )}

        {/* ═══ PUBLISH TAB ═══ */}
        {activeTab === 'publish' && (
          <section className="bg-gray-800 border border-gray-700 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-300 mb-4">Publish Video</h3>

            {/* Platform Selection */}
            {content.videoUrl && content.status !== 'published' && (
              <div className="mb-4">
                <p className="text-xs text-gray-400 mb-2">Select platforms:</p>
                <div className="flex gap-4">
                  {[
                    { key: 'youtube', label: 'YouTube', icon: '▶️' },
                    { key: 'instagram', label: 'Instagram', icon: '📷' },
                    { key: 'tiktok', label: 'TikTok', icon: '🎵' }
                  ].map(({ key, label, icon }) => (
                    <label key={key} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={videoPlatforms[key]}
                        onChange={(e) => setVideoPlatforms(prev => ({ ...prev, [key]: e.target.checked }))}
                        className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-red-600 focus:ring-red-500"
                      />
                      <span className="text-sm text-gray-300">{icon} {label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-3 flex-wrap">
              {/* Publish Video */}
              {content.videoUrl && content.status !== 'published' && (
                <button
                  onClick={handlePublishVideo}
                  disabled={publishingVideo || !Object.values(videoPlatforms).some(v => v) || caption.length <= 10}
                  className="bg-red-600 hover:bg-red-700 disabled:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm transition-colors"
                  title={caption.length <= 10 ? 'Add a caption first' : ''}
                >
                  {publishingVideo ? 'Publishing...' : 'Publish Video'}
                </button>
              )}
              {content.status === 'published' && (
                <span className="bg-green-600/20 text-green-400 px-4 py-2 rounded-lg text-sm">
                  Published
                </span>
              )}
              {content.videoUrl && (
                <a
                  href={content.videoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm transition-colors"
                >
                  Download Video
                </a>
              )}
              <button
                onClick={() => navigator.clipboard.writeText(content.videoUrl || '')}
                className="bg-gray-700 hover:bg-gray-600 text-gray-200 px-4 py-2 rounded-lg text-sm transition-colors"
              >
                Copy URL
              </button>
            </div>

            {caption.length <= 10 && content.status !== 'published' && (
              <p className="text-xs text-yellow-400 mt-4">Complete the Caption tab before publishing</p>
            )}
          </section>
        )}

        {/* Sticky Workflow Progress Bar */}
        <div className="sticky bottom-0 bg-gray-900 border-t border-gray-700 p-4 mt-6 -mx-6 -mb-6 rounded-b-lg">
          <div className="flex justify-between items-center">
            <WorkflowProgress tabs={tabs} />
            {content.status === 'published' ? (
              <span className="bg-green-600/20 text-green-400 px-4 py-2 rounded-lg text-sm">
                Published
              </span>
            ) : (
              <button
                onClick={() => setActiveTab('publish')}
                disabled={caption.length <= 10}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  caption.length > 10
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                }`}
              >
                Ready to Publish
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Error Banner */}
      {error && (
        <div className="bg-red-900/50 border border-red-700 text-red-300 px-4 py-3 rounded-lg flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-red-400 hover:text-red-200">✕</button>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-start">
        <div className="flex gap-4">
          {/* Thumbnail */}
          {content.selectedImageUrl && (
            <div className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden bg-gray-900">
              <img
                src={content.selectedImageUrl}
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
          )}
          <div>
            <button
              onClick={() => navigate('/admin/content-ops')}
              className="text-gray-400 hover:text-gray-300 text-sm mb-2 inline-block"
            >
              ← Back to Content Ops
            </button>
            <h1 className="text-2xl font-bold text-gray-100">{content.topic}</h1>
            <div className="flex items-center gap-3 mt-2">
              <span className={`text-xs px-2 py-0.5 rounded ${
                content.status === 'published' ? 'bg-green-600/20 text-green-400' : 'bg-yellow-600/20 text-yellow-400'
              }`}>
                {content.status}
              </span>
              <span className="text-xs text-gray-500 bg-gray-700 px-2 py-0.5 rounded">
                {FORMAT_LABELS[content.format] || content.format}
              </span>
              <span className="text-xs text-gray-500">v{content.version || 1}</span>
              {content.repurposedFrom && (
                <button
                  onClick={() => navigate(`/admin/content-ops/editor/${content.repurposedFrom}`)}
                  className="text-xs text-blue-400 hover:text-blue-300"
                >
                  ← Parent content
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {dirty && (
            <span className="text-xs text-yellow-400 self-center mr-2">Unsaved changes</span>
          )}
          <button
            onClick={handleSave}
            disabled={saving || !dirty}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm transition-colors"
          >
            {saving ? 'Saving...' : 'Save Draft'}
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      {tabs.length > 0 && (
        <EditorTabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      )}

      {/* === SCRIPT TAB (YT Scripts) === */}
      {activeTab === 'script' && ['yt_script', 'yt_script_short', 'yt_script_long'].includes(content.format) && (
        <section className="bg-gray-800 border border-gray-700 rounded-lg p-4 space-y-4">
          {/* Section Header with Save Button */}
          <div className="flex justify-between items-center pb-2 border-b border-gray-700">
            <h3 className="text-lg font-medium text-gray-200">Video Script Editor</h3>
            <button
              onClick={handleSaveCreative}
              disabled={saving || !dirty}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-4 py-1.5 rounded-lg text-sm transition-colors"
            >
              {saving ? 'Saving...' : 'Save Script'}
            </button>
          </div>

          {/* Title & Hook - Most Prominent */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <h3 className="text-sm font-medium text-gray-300">Video Title & Hook</h3>
              <span className="text-xs text-gray-500">These drive clicks and retention</span>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Title</label>
                <input
                  type="text"
                  value={editableTitle}
                  onChange={(e) => { setEditableTitle(e.target.value); setDirty(true); }}
                  placeholder="Compelling video title..."
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-600 rounded-lg text-gray-100 text-lg font-medium focus:outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Hook (first 10 seconds)</label>
                <input
                  type="text"
                  value={editableHook}
                  onChange={(e) => { setEditableHook(e.target.value); setDirty(true); }}
                  placeholder="Opening line that grabs attention..."
                  className="w-full px-4 py-2 bg-gray-900 border border-orange-600/50 rounded-lg text-gray-100 focus:outline-none focus:border-orange-500"
                />
              </div>
            </div>
          </div>

          {/* Spoken Script - For TTS */}
          <div className="border-t border-gray-700 pt-4 space-y-2">
            <div className="flex justify-between items-center">
              <h3 className="text-sm font-medium text-gray-300">Spoken Script (TTS)</h3>
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-500">{wordCount(editableSpokenScript)} words</span>
                <button
                  onClick={syncScriptToScenes}
                  disabled={!editableSpokenScript.trim()}
                  className="text-xs text-purple-400 hover:text-purple-300 disabled:text-gray-600 disabled:cursor-not-allowed transition-colors"
                  title="Split script into scene narrations by paragraphs"
                >
                  Sync to scenes
                </button>
              </div>
            </div>
            <textarea
              rows={12}
              value={editableSpokenScript}
              onChange={(e) => { setEditableSpokenScript(e.target.value); setDirty(true); }}
              placeholder="Full narration text that will be converted to speech..."
              className="w-full px-4 py-3 bg-gray-900 border border-blue-600/30 rounded-lg text-gray-100 text-sm focus:outline-none focus:border-blue-500 resize-y"
            />
            <p className="text-xs text-gray-500">This text goes to ElevenLabs for voiceover generation. Click "Sync to scenes" to distribute by paragraphs.</p>
          </div>

          {/* Video Pacing - Scene-by-scene */}
          <div className="border-t border-gray-700 pt-4 space-y-3">
            <div className="flex justify-between items-center">
              <h3 className="text-sm font-medium text-gray-300">Video Pacing</h3>
              <button
                onClick={addVideoPacingScene}
                className="text-xs text-green-400 hover:text-green-300"
              >
                + Add Scene
              </button>
            </div>
            <p className="text-xs text-gray-500">Editorial guidance for video editing. Timestamps are approximate.</p>

            {editableVideoPacing.length === 0 ? (
              <div className="bg-gray-900 rounded-lg p-4 text-center">
                <p className="text-sm text-gray-500">No video pacing defined.</p>
                <button
                  onClick={addVideoPacingScene}
                  className="mt-2 text-xs text-blue-400 hover:text-blue-300"
                >
                  + Add first scene
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {editableVideoPacing.map((scene, idx) => (
                  <div key={idx} className="bg-gray-900 rounded-lg p-3 space-y-2 border border-gray-700">
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-medium text-purple-400">Scene {idx + 1}</span>
                      <button
                        onClick={() => removeVideoPacingScene(idx)}
                        className="text-xs text-red-400 hover:text-red-300"
                      >
                        Remove
                      </button>
                    </div>

                    {/* Timing Row */}
                    <div className="flex gap-3">
                      <div className="w-24">
                        <label className="block text-xs text-gray-500 mb-1">Start</label>
                        <input
                          type="text"
                          value={scene.startTime || ''}
                          onChange={(e) => updateVideoPacingScene(idx, 'startTime', e.target.value)}
                          placeholder="0:00"
                          className="w-full px-2 py-1 bg-gray-800 border border-gray-600 rounded text-gray-300 text-xs focus:outline-none focus:border-purple-500"
                        />
                      </div>
                      <div className="w-24">
                        <label className="block text-xs text-gray-500 mb-1">End</label>
                        <input
                          type="text"
                          value={scene.endTime || ''}
                          onChange={(e) => updateVideoPacingScene(idx, 'endTime', e.target.value)}
                          placeholder="0:30"
                          className="w-full px-2 py-1 bg-gray-800 border border-gray-600 rounded text-gray-300 text-xs focus:outline-none focus:border-purple-500"
                        />
                      </div>
                      <div className="w-24">
                        <label className="block text-xs text-gray-500 mb-1">Transition</label>
                        <select
                          value={scene.transition || 'cut'}
                          onChange={(e) => updateVideoPacingScene(idx, 'transition', e.target.value)}
                          className="w-full px-2 py-1 bg-gray-800 border border-gray-600 rounded text-gray-300 text-xs focus:outline-none focus:border-purple-500"
                        >
                          <option value="cut">Cut</option>
                          <option value="fade">Fade</option>
                          <option value="zoom">Zoom</option>
                        </select>
                      </div>
                      <div className="flex-1">
                        <label className="block text-xs text-gray-500 mb-1">Text Overlay</label>
                        <input
                          type="text"
                          value={scene.textOverlay || ''}
                          onChange={(e) => updateVideoPacingScene(idx, 'textOverlay', e.target.value || null)}
                          placeholder="Optional on-screen text..."
                          className="w-full px-2 py-1 bg-gray-800 border border-gray-600 rounded text-gray-300 text-xs focus:outline-none focus:border-purple-500"
                        />
                      </div>
                    </div>

                    {/* Narration */}
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Narration</label>
                      <textarea
                        rows={2}
                        value={scene.narration || ''}
                        onChange={(e) => updateVideoPacingScene(idx, 'narration', e.target.value)}
                        placeholder="Spoken text for this scene..."
                        className="w-full px-2 py-1.5 bg-gray-800 border border-blue-600/30 rounded text-gray-300 text-xs focus:outline-none focus:border-blue-500 resize-y"
                      />
                    </div>

                    {/* Visual Direction */}
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Visual Direction</label>
                      <textarea
                        rows={2}
                        value={scene.visual || ''}
                        onChange={(e) => updateVideoPacingScene(idx, 'visual', e.target.value)}
                        placeholder="B-roll, graphics, or visual guidance..."
                        className="w-full px-2 py-1.5 bg-gray-800 border border-yellow-600/30 rounded text-gray-300 text-xs focus:outline-none focus:border-yellow-500 resize-y"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* === THUMBNAIL TAB (YT Scripts) === */}
      {activeTab === 'thumbnail' && ['yt_script', 'yt_script_short', 'yt_script_long'].includes(content.format) && (
        <section className="bg-gray-800 border border-gray-700 rounded-lg p-4 space-y-4">
          <div className="flex justify-between items-center pb-2 border-b border-gray-700">
            <h3 className="text-lg font-medium text-gray-200">Thumbnail & Image</h3>
            <button
              onClick={handleSaveCreative}
              disabled={saving || !dirty}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-4 py-1.5 rounded-lg text-sm transition-colors"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>

          {/* Image Prompt */}
          <div className="bg-yellow-900/20 border border-yellow-700/50 rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs px-2 py-0.5 bg-yellow-600/30 text-yellow-400 rounded font-medium">IMAGE PROMPT</span>
              <button
                onClick={() => {
                  setImagePrompt(editableImagePrompt);
                  setImagePreset('social_card');
                }}
                className="text-xs text-blue-400 hover:text-blue-300"
              >
                Use for Generation
              </button>
            </div>
            <textarea
              rows={3}
              value={editableImagePrompt}
              onChange={(e) => { setEditableImagePrompt(e.target.value); setDirty(true); }}
              className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded text-gray-200 text-sm focus:outline-none focus:border-yellow-500 resize-y"
              placeholder="Describe the thumbnail/image..."
            />
          </div>

          {/* Thumbnail Ideas */}
          <div className="bg-green-900/20 border border-green-700/50 rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs px-2 py-0.5 bg-green-600/30 text-green-400 rounded font-medium">THUMBNAIL IDEAS</span>
              <button
                onClick={addThumbnailIdea}
                className="text-xs text-green-400 hover:text-green-300"
              >
                + Add Idea
              </button>
            </div>
            <div className="space-y-2">
              {editableThumbnailIdeas.map((idea, idx) => (
                <div key={idx} className="flex gap-2">
                  <span className="text-xs text-gray-500 mt-2 w-4">{idx + 1}.</span>
                  <textarea
                    rows={2}
                    value={idea}
                    onChange={(e) => updateThumbnailIdea(idx, e.target.value)}
                    className="flex-1 px-3 py-2 bg-gray-900 border border-gray-600 rounded text-gray-200 text-sm focus:outline-none focus:border-green-500 resize-y"
                  />
                  <button
                    onClick={() => removeThumbnailIdea(idx)}
                    className="text-red-400 hover:text-red-300 text-xs self-start mt-2"
                  >
                    x
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Image Generation */}
          <div className="border-t border-gray-700 pt-4 space-y-3">
            <h4 className="text-sm font-medium text-gray-300">Generate Image</h4>
            <div className="flex gap-3">
              <input
                type="text"
                value={imagePrompt}
                onChange={(e) => setImagePrompt(e.target.value)}
                placeholder="Describe the image you want..."
                className="flex-1 px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-100 text-sm focus:outline-none focus:border-blue-500"
              />
              <select
                value={imagePreset}
                onChange={(e) => setImagePreset(e.target.value)}
                className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-100 text-sm focus:outline-none focus:border-blue-500"
              >
                {IMAGE_PRESETS.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
              <button
                onClick={handleGenerateImage}
                disabled={generatingImage || !imagePrompt.trim()}
                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm transition-colors whitespace-nowrap"
              >
                {generatingImage ? 'Generating...' : 'Generate'}
              </button>
            </div>

            {/* Upload Custom Image */}
            <div className="border-t border-gray-700 pt-4 mt-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-gray-300">Or Upload Your Own</h4>
                  <p className="text-xs text-gray-500">Uploaded image will be set as selected</p>
                </div>
                <label className={`inline-block px-4 py-2 rounded-lg text-sm transition-colors cursor-pointer ${
                  uploadingImage
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                }`}>
                  {uploadingImage ? 'Uploading...' : 'Upload Image'}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    onChange={handleUploadCustomImage}
                    disabled={uploadingImage}
                    className="hidden"
                  />
                </label>
              </div>
            </div>

            {/* Image Gallery */}
            <ImageGallery
              images={content.images || []}
              selectedImageUrl={content.selectedImageUrl}
              onSelect={handleSelectImage}
            />
          </div>
        </section>
      )}

      {/* === QUEUE TAB (YT Scripts) === */}
      {activeTab === 'queue' && ['yt_script', 'yt_script_short', 'yt_script_long'].includes(content.format) && (
        <div className="space-y-4">
          {/* Not in queue yet */}
          {!content.videoQueueId ? (
            <section className="bg-gray-800 border border-gray-700 rounded-lg p-4 space-y-4">
              <h3 className="text-lg font-medium text-gray-200">Video Queue</h3>
              <p className="text-sm text-gray-400">
                Send this script to the video queue to start the video generation pipeline.
              </p>
              <button
                onClick={handleSendToVideoQueue}
                disabled={sendingToQueue}
                className="bg-orange-600 hover:bg-orange-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm transition-colors"
              >
                {sendingToQueue ? 'Sending...' : 'Send to Video Queue'}
              </button>
            </section>
          ) : (
            <>
              {/* Pipeline Status Header */}
              <section className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-200">Video Pipeline</h3>
                  <span className={`text-xs px-3 py-1.5 rounded-lg font-medium ${
                    videoQueueData?.status === 'published' ? 'bg-green-600/20 text-green-400' :
                    videoQueueData?.status === 'broll_mixed' ? 'bg-purple-600/20 text-purple-400' :
                    videoQueueData?.status === 'avatar_ready' ? 'bg-blue-600/20 text-blue-400' :
                    videoQueueData?.status === 'voice_ready' ? 'bg-cyan-600/20 text-cyan-400' :
                    'bg-orange-600/20 text-orange-400'
                  }`}>
                    {videoQueueData?.status === 'published' ? 'Published' :
                     videoQueueData?.status === 'broll_mixed' ? 'B-Roll Mixed' :
                     videoQueueData?.status === 'avatar_ready' ? 'Avatar Ready' :
                     videoQueueData?.status === 'voice_ready' ? 'Voiceover Ready' :
                     'In Queue'}
                  </span>
                </div>
                {/* Pipeline Progress */}
                <div className="flex items-center gap-2 text-xs">
                  <span className={videoQueueData?.voiceoverUrl ? 'text-green-400' : 'text-gray-500'}>
                    Voiceover {videoQueueData?.voiceoverUrl ? '✓' : '○'}
                  </span>
                  <span className="text-gray-600">→</span>
                  <span className={videoQueueData?.avatarVideoUrl || videoQueueData?.selfRecordingUrl ? 'text-green-400' : 'text-gray-500'}>
                    Video {videoQueueData?.avatarVideoUrl || videoQueueData?.selfRecordingUrl ? '✓' : '○'}
                  </span>
                  <span className="text-gray-600">→</span>
                  <span className={videoQueueData?.brollMixedUrl ? 'text-green-400' : 'text-gray-500'}>
                    B-Roll {videoQueueData?.brollMixedUrl ? '✓' : '○'}
                  </span>
                  <span className="text-gray-600">→</span>
                  <span className={videoQueueData?.status === 'published' ? 'text-green-400' : 'text-gray-500'}>
                    Publish {videoQueueData?.status === 'published' ? '✓' : '○'}
                  </span>
                </div>
              </section>

              {/* Step 1: Voiceover */}
              <section className="bg-gray-800 border border-gray-700 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-gray-300">1. Voiceover</h4>
                  {videoQueueData?.voiceoverUrl && (
                    <span className="text-xs text-green-400">Generated</span>
                  )}
                </div>
                {videoQueueData?.voiceoverUrl ? (
                  <audio controls src={videoQueueData.voiceoverUrl} className="w-full" />
                ) : (
                  <button
                    onClick={handleGenerateVoiceover}
                    disabled={generatingVoiceover}
                    className="bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm transition-colors"
                  >
                    {generatingVoiceover ? 'Generating Voiceover...' : 'Generate Voiceover'}
                  </button>
                )}
              </section>

              {/* Step 2: Video (Avatar or Self Recording) */}
              <section className="bg-gray-800 border border-gray-700 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-gray-300">2. Video</h4>
                  {(videoQueueData?.avatarVideoUrl || videoQueueData?.selfRecordingUrl) && (
                    <span className="text-xs text-green-400">
                      {videoQueueData?.selfRecordingUrl ? 'Self Recording' : 'Avatar Generated'}
                    </span>
                  )}
                </div>

                {/* Show existing video */}
                {(videoQueueData?.avatarVideoUrl || videoQueueData?.selfRecordingUrl) ? (
                  <video
                    controls
                    src={videoQueueData.selfRecordingUrl || videoQueueData.avatarVideoUrl}
                    className="w-full rounded max-h-[300px] bg-black"
                  />
                ) : (
                  <div className="space-y-3">
                    <p className="text-xs text-gray-500">Generate an AI avatar video or upload your own recording</p>
                    <div className="flex gap-3 flex-wrap">
                      <button
                        onClick={handleGenerateAvatar}
                        disabled={generatingAvatar || !videoQueueData?.voiceoverUrl}
                        className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm transition-colors"
                        title={!videoQueueData?.voiceoverUrl ? 'Generate voiceover first' : ''}
                      >
                        {generatingAvatar ? 'Generating Avatar...' : 'Generate Avatar Video'}
                      </button>
                      <label className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm transition-colors cursor-pointer">
                        {uploadingSelfRecording ? 'Uploading...' : 'Upload Self Recording'}
                        <input
                          type="file"
                          accept="video/*"
                          onChange={handleUploadSelfRecording}
                          disabled={uploadingSelfRecording}
                          className="hidden"
                        />
                      </label>
                    </div>
                    {!videoQueueData?.voiceoverUrl && (
                      <p className="text-xs text-yellow-400">Generate voiceover first before creating avatar video</p>
                    )}
                  </div>
                )}
              </section>

              {/* Step 3: B-Roll Mix */}
              <section className="bg-gray-800 border border-gray-700 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-gray-300">3. B-Roll Mix</h4>
                  {videoQueueData?.brollMixedUrl && (
                    <span className="text-xs text-green-400">Mixed</span>
                  )}
                </div>

                {videoQueueData?.brollMixedUrl ? (
                  <div className="space-y-3">
                    <video
                      controls
                      src={videoQueueData.brollMixedUrl}
                      className="w-full rounded max-h-[300px] bg-black"
                    />
                    <a
                      href={videoQueueData.brollMixedUrl}
                      download
                      className="inline-block bg-gray-700 hover:bg-gray-600 text-gray-200 px-3 py-1.5 rounded text-xs transition-colors"
                    >
                      Download Final Video
                    </a>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-500">
                      B-Roll mixing adds visual overlays, lower thirds, and effects to your video.
                    </p>
                    {(videoQueueData?.avatarVideoUrl || videoQueueData?.selfRecordingUrl) ? (
                      <p className="text-xs text-yellow-400">
                        Use <code className="bg-gray-900 px-1 rounded">/broll-mix</code> skill to mix B-roll for this video.
                      </p>
                    ) : (
                      <p className="text-xs text-gray-500">Complete step 2 first</p>
                    )}
                  </div>
                )}
              </section>

              {/* Step 4: Publish */}
              <section className="bg-gray-800 border border-gray-700 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-gray-300">4. Publish</h4>
                  {videoQueueData?.status === 'published' && (
                    <span className="text-xs text-green-400">Published</span>
                  )}
                </div>

                {videoQueueData?.status === 'published' ? (
                  <div className="space-y-2">
                    <p className="text-sm text-green-400">This video has been published!</p>
                    {videoQueueData?.publishedChannels && (
                      <div className="flex gap-2">
                        {videoQueueData.publishedChannels.map(ch => (
                          <span key={ch} className="text-xs bg-green-600/20 text-green-400 px-2 py-1 rounded">
                            {ch}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-xs text-gray-500">
                      {videoQueueData?.brollMixedUrl
                        ? 'Ready to publish to social platforms'
                        : 'Complete B-Roll mix before publishing'}
                    </p>
                    <button
                      onClick={handlePublishVideo}
                      disabled={!videoQueueData?.brollMixedUrl || publishingVideo}
                      className="bg-red-600 hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm transition-colors"
                    >
                      {publishingVideo ? 'Publishing...' : 'Publish Video'}
                    </button>
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      )}

      {/* === CONTENT TAB (Blogs, Tweets, IG, etc.) === */}
      {activeTab === 'content' && !['yt_script', 'yt_script_short', 'yt_script_long'].includes(content.format) && (
        <>
          {/* Tweet Thread Editor */}
          {content.format === 'tweet_thread' && (
            <TweetThreadEditor
              body={body}
              onBodyChange={(newBody) => { setBody(newBody); setDirty(true); }}
              onSave={handleSave}
              onPostThread={handlePostThread}
              postingThread={postingThread}
              saving={saving}
              dirty={dirty}
            />
          )}

          {/* Body Editor */}
          {content.format !== 'tweet_thread' && (
            <section className="bg-gray-800 border border-gray-700 rounded-lg p-4">
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-medium text-gray-300">Content Body</label>
                {content.format === 'tweet' && (
                  <span className={`text-xs ${body.length > 260 ? 'text-red-400' : 'text-gray-500'}`}>
                    {body.length}/280
                  </span>
                )}
              </div>
              <textarea
                rows={content.format === 'tweet' ? 6 : 16}
                value={body}
                onChange={(e) => { setBody(e.target.value); setDirty(true); }}
                className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-gray-100 text-sm font-mono focus:outline-none focus:border-blue-500 resize-y"
                placeholder="Content body..."
              />
            </section>
          )}
        </>
      )}

      {/* === IMAGE TAB (Blogs, Tweets, IG) === */}
      {activeTab === 'image' && !['yt_script', 'yt_script_short', 'yt_script_long'].includes(content.format) && (
        <section className="bg-gray-800 border border-gray-700 rounded-lg p-4 space-y-4">
          <h3 className="text-sm font-medium text-gray-300">Image Generation</h3>

          {content.suggestedImagePrompt && !content.images?.length && (
            <p className="text-xs text-amber-400 mb-1">Suggested prompt from content research (edit before generating)</p>
          )}

          {/* Reference Image Upload */}
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1">Reference Image (optional)</label>
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={handleReferenceImageChange}
                  className="text-xs text-gray-400 file:mr-2 file:py-1 file:px-3 file:rounded file:border-0 file:text-xs file:bg-gray-700 file:text-gray-300 hover:file:bg-gray-600"
                />
                {referenceImage && (
                  <button
                    onClick={clearReferenceImage}
                    className="text-xs text-red-400 hover:text-red-300"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
            {referenceImage && (
              <div className="w-16 h-16 rounded overflow-hidden border border-gray-600">
                <img
                  src={referenceImage.preview}
                  alt="Reference"
                  className="w-full h-full object-cover"
                />
              </div>
            )}
          </div>

          <div className="flex gap-3">
            <input
              type="text"
              value={imagePrompt}
              onChange={(e) => setImagePrompt(e.target.value)}
              placeholder="Describe the image you want..."
              className="flex-1 px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-100 text-sm focus:outline-none focus:border-blue-500"
            />
            <select
              value={imagePreset}
              onChange={(e) => setImagePreset(e.target.value)}
              className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-100 text-sm focus:outline-none focus:border-blue-500"
            >
              {IMAGE_PRESETS.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
            <button
              onClick={handleGenerateImage}
              disabled={generatingImage || !imagePrompt.trim()}
              className="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm transition-colors whitespace-nowrap"
            >
              {generatingImage ? 'Generating...' : 'Generate'}
            </button>
          </div>

          {/* Upload Custom Image */}
          <div className="border-t border-gray-700 pt-4 mt-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-gray-300">Or Upload Your Own</h4>
                <p className="text-xs text-gray-500">Uploaded image will be set as selected</p>
              </div>
              <label className={`inline-block px-4 py-2 rounded-lg text-sm transition-colors cursor-pointer ${
                uploadingImage
                  ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-700 hover:bg-gray-600 text-gray-200'
              }`}>
                {uploadingImage ? 'Uploading...' : 'Upload Image'}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  onChange={handleUploadCustomImage}
                  disabled={uploadingImage}
                  className="hidden"
                />
              </label>
            </div>
          </div>

          {/* Image Gallery */}
          <ImageGallery
            images={content.images || []}
            selectedImageUrl={content.selectedImageUrl}
            onSelect={handleSelectImage}
          />

          {/* Crop to Size */}
          {content.selectedImageUrl && (
            <div className="border-t border-gray-700 pt-4 space-y-3">
              <h4 className="text-sm font-medium text-gray-300">Crop to Size</h4>
              <div className="flex gap-3">
                <select
                  value={cropPreset}
                  onChange={(e) => setCropPreset(e.target.value)}
                  className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-100 text-sm focus:outline-none focus:border-purple-500"
                >
                  <option value="blog_hero">Blog Hero (1200x630)</option>
                  <option value="social_card">Social Card (1200x675)</option>
                  <option value="ig_post">IG Post (1080x1080)</option>
                  <option value="ig_story">IG Story (1080x1920)</option>
                  <option value="thumbnail">Thumbnail (1280x720)</option>
                  <option value="tweet_media">Tweet Media (1200x675)</option>
                </select>
                <button
                  onClick={handleCropImage}
                  disabled={croppingImage}
                  className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm transition-colors whitespace-nowrap"
                >
                  {croppingImage ? 'Cropping...' : 'Crop'}
                </button>
              </div>

              {/* Display cropped images */}
              {(croppedImages.length > 0 || content.croppedImages?.length > 0) && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {(croppedImages.length > 0 ? croppedImages : content.croppedImages || []).map((img, idx) => (
                    <div key={idx} className="bg-gray-900 rounded-lg p-2 space-y-2">
                      <div className="aspect-video relative overflow-hidden rounded bg-gray-800">
                        <img
                          src={img.url}
                          alt={img.preset}
                          className="absolute inset-0 w-full h-full object-contain"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-400">{img.preset.replace('_', ' ')}</span>
                        <span className="text-xs text-gray-500">{img.width}x{img.height}</span>
                      </div>
                      <button
                        onClick={() => navigator.clipboard.writeText(img.url)}
                        className="w-full text-xs text-blue-400 hover:text-blue-300 py-1"
                      >
                        Copy URL
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {/* === PUBLISH TAB === */}
      {activeTab === 'publish' && (
        <section className="bg-gray-800 border border-gray-700 rounded-lg p-4 space-y-4">
          <h3 className="text-lg font-medium text-gray-200">Publish</h3>

          {/* Tweet/Tweet Thread → Twitter */}
          {(content.format === 'tweet' || content.format === 'tweet_thread') && (
            <div className="space-y-3">
              <p className="text-sm text-gray-400">
                {content.format === 'tweet_thread'
                  ? 'Post this thread to Twitter (@TrendingChess).'
                  : 'Post this tweet to Twitter (@TrendingChess).'}
              </p>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    setSelectedChannels(['twitter']);
                    handlePublish();
                  }}
                  disabled={publishing || content.status === 'published'}
                  className="bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm transition-colors"
                >
                  {publishing ? 'Posting...' : content.format === 'tweet_thread' ? 'Post Thread to Twitter' : 'Post to Twitter'}
                </button>
                {content.status === 'published' && (
                  <span className="text-xs text-green-400">Posted</span>
                )}
              </div>
            </div>
          )}

          {/* Blog → Blog publish with author/tags */}
          {content.format === 'blog' && (
            <div className="space-y-4">
              <div className="flex gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Author</label>
                  <input
                    type="text"
                    value={author}
                    onChange={(e) => setAuthor(e.target.value)}
                    className="w-48 px-3 py-1.5 bg-gray-700 border border-gray-600 rounded-lg text-gray-100 text-sm focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">Tags (comma-separated)</label>
                  <input
                    type="text"
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                    placeholder="chess, strategy, openings"
                    className="w-full px-3 py-1.5 bg-gray-700 border border-gray-600 rounded-lg text-gray-100 text-sm focus:outline-none focus:border-blue-500"
                  />
                </div>
              </div>
              <button
                onClick={() => {
                  setSelectedChannels(['blog']);
                  handlePublish();
                }}
                disabled={publishing || content.status === 'published'}
                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm transition-colors"
              >
                {publishing ? 'Publishing...' : 'Publish to Blog'}
              </button>
            </div>
          )}

          {/* IG → Instagram */}
          {content.format === 'ig' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-400">
                Instagram publishing available via n8n/Post-Bridge integration.
              </p>
            </div>
          )}

          {/* Blog URL */}
          {content.blogUrl && (
            <div className="border-t border-gray-700 pt-3">
              <p className="text-xs text-gray-500 mb-1">Blog published:</p>
              <a
                href={content.blogUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-400 hover:text-blue-300 underline"
              >
                {content.blogUrl}
              </a>
            </div>
          )}

          {/* Published History */}
          {content.publishedChannels?.length > 0 && (
            <div className="border-t border-gray-700 pt-3">
              <p className="text-xs text-gray-500 mb-2">Published:</p>
              <div className="flex flex-wrap gap-2">
                {content.publishedChannels.map((pc, idx) => (
                  <span key={idx} className="text-xs bg-green-600/20 text-green-400 px-2 py-1 rounded">
                    {pc.channel} - {formatDate(pc.publishedAt)}
                  </span>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* === EXTRAS TAB === */}
      {activeTab === 'extras' && (
        <>
          {/* AI Respin */}
          <section className="bg-gray-800 border border-gray-700 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-300 mb-3">AI Respin</h3>
            <div className="flex gap-3">
              <input
                type="text"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !respinning && handleRespin()}
                placeholder="e.g. Make the intro punchier, add more stats..."
                className="flex-1 px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-100 text-sm focus:outline-none focus:border-blue-500"
              />
              <button
                onClick={handleRespin}
                disabled={respinning || !feedback.trim()}
                className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm transition-colors whitespace-nowrap"
              >
                {respinning ? 'Respinning...' : 'Respin'}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Provide feedback and Gemini will revise the content. Current edits are included.
            </p>
          </section>

          {/* Version History */}
          <VersionHistory
            versions={content.versions || []}
            currentVersion={content.version || 1}
            onRestore={handleRestore}
          />

          {/* Raw JSON for YT Scripts */}
          {['yt_script', 'yt_script_short', 'yt_script_long'].includes(content.format) && (
            <section className="bg-gray-800 border border-gray-700 rounded-lg p-4">
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-medium text-gray-300">Raw JSON (Advanced)</label>
                <span className="text-xs text-gray-500">Use the Script tab for easier editing</span>
              </div>
              <textarea
                rows={6}
                value={body}
                onChange={(e) => { setBody(e.target.value); setDirty(true); }}
                className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-gray-100 text-sm font-mono focus:outline-none focus:border-blue-500 resize-y"
                placeholder="Content body..."
              />
            </section>
          )}

          {/* Repurposing (visible for blog-format content and tweet_thread) */}
          {(content.format === 'blog' || content.format === 'ig' || content.format === 'tweet_thread') && (
            <section className="bg-gray-800 border border-gray-700 rounded-lg p-4 space-y-4">
              <h3 className="text-sm font-medium text-gray-300">Repurpose Content</h3>
              <p className="text-xs text-gray-500">
                Transform this {FORMAT_LABELS[content.format]?.toLowerCase()} into other formats. Each creates a new draft you can edit independently.
              </p>

              <div className="flex gap-3 flex-wrap">
                {[
                  { format: 'tweet_thread', label: 'Tweet Thread' },
                  { format: 'yt_script_short', label: 'Short Video' },
                  { format: 'yt_script_long', label: 'Long Video' },
                  { format: 'idea_board', label: 'New Ideas' }
                ]
                  .filter(btn => btn.format !== content.format)
                  .map(btn => (
                  <button
                    key={btn.format}
                    onClick={() => handleRepurpose(btn.format)}
                    disabled={repurposing !== null}
                    className={`${btn.format === 'idea_board' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-indigo-600 hover:bg-indigo-700'} disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm transition-colors`}
                  >
                    {repurposing === btn.format ? 'Creating...' : btn.label}
                  </button>
                ))}
              </div>

              {/* Repurposed Items */}
              {repurposedItems.length > 0 && (
                <div className="border-t border-gray-700 pt-3 space-y-2">
                  <p className="text-xs text-gray-500">Repurposed from this content:</p>
                  {repurposedItems.map(item => (
                    <div key={item.id} className="flex justify-between items-center bg-gray-900 rounded px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs bg-indigo-600/20 text-indigo-400 px-2 py-0.5 rounded">
                          {FORMAT_LABELS[item.format] || item.format}
                        </span>
                        <span className="text-xs text-gray-400 truncate max-w-xs">{item.topic}</span>
                      </div>
                      <button
                        onClick={() => window.open(`/admin/content-ops/editor/${item.id}`, '_blank')}
                        className="text-xs text-blue-400 hover:text-blue-300"
                      >
                        Open
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}
        </>
      )}

      {/* Sticky Workflow Progress Bar */}
      {tabs.length > 0 && (
        <div className="sticky bottom-0 bg-gray-900/95 backdrop-blur border-t border-gray-700 p-3 -mx-4 px-4 mt-6">
          <div className="flex justify-between items-center">
            <WorkflowProgress tabs={tabs} />
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentEditor;
